import React, { useState } from 'react';
import { useWorkoutsContext } from '../hooks/useWorkoutsContext';
import { formatDistanceToNow } from 'date-fns';

const WorkoutDetails = ({ workout, activeUnit, onEditClick, onShowToast }) => {
  const { dispatch } = useWorkoutsContext();
  const [isUpdating, setIsUpdating] = useState(false);

  // Delete workout
  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete "${workout.title}"?`)) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/workouts/${workout._id}`, {
        method: 'DELETE'
      });
      const json = await response.json();

      if (response.ok) {
        dispatch({ type: 'DELETE_WORKOUT', payload: json });
        onShowToast(`Deleted "${workout.title}" successfully`, 'info');
      } else {
        onShowToast(json.error || 'Failed to delete workout', 'error');
      }
    } catch (err) {
      onShowToast('Connection error. Failed to delete.', 'error');
    }
  };

  // Toggle Favorite/Pin status
  const handleToggleFavorite = async () => {
    if (isUpdating) return;
    setIsUpdating(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/workouts/${workout._id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ isFavorite: !workout.isFavorite })
      });
      const json = await response.json();

      if (response.ok) {
        dispatch({ type: 'UPDATE_WORKOUT', payload: json });
        onShowToast(
          json.isFavorite ? `Pinned "${workout.title}" to top` : `Unpinned "${workout.title}"`,
          'success'
        );
      } else {
        onShowToast(json.error || 'Failed to update favorite status', 'error');
      }
    } catch (err) {
      onShowToast('Connection error. Failed to update status.', 'error');
    } finally {
      setIsUpdating(false);
    }
  };

  // Unit Conversion for Display
  const getDisplayLoad = () => {
    const loadVal = Number(workout.load) || 0;
    const workoutUnit = workout.weightUnit || 'lbs';

    if (workoutUnit === activeUnit) {
      return `${loadVal} ${activeUnit}`;
    }
    if (workoutUnit === 'lbs' && activeUnit === 'kg') {
      return `${(loadVal / 2.20462).toFixed(1)} kg`;
    }
    if (workoutUnit === 'kg' && activeUnit === 'lbs') {
      return `${(loadVal * 2.20462).toFixed(1)} lbs`;
    }
    return `${loadVal} ${activeUnit}`;
  };

  // Safe Date parsing
  const getFormattedDate = () => {
    try {
      const dateObj = new Date(workout.date || workout.createdAt);
      if (isNaN(dateObj.getTime())) return 'Recently';
      return formatDistanceToNow(dateObj, { addSuffix: true });
    } catch (e) {
      return 'Recently';
    }
  };

  const categoryClass = (workout.category || 'Strength').toLowerCase();

  return (
    <div className={`workout-details ${categoryClass}`}>
      {/* Top action buttons */}
      <div className="card-actions">
        {/* Favorite/Pin Button */}
        <button
          className={`action-btn fav-btn ${workout.isFavorite ? 'active' : ''}`}
          onClick={handleToggleFavorite}
          title={workout.isFavorite ? 'Unpin workout' : 'Pin workout'}
          disabled={isUpdating}
        >
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
          </svg>
        </button>

        {/* Edit Button */}
        <button
          className="action-btn edit-btn"
          onClick={() => onEditClick(workout)}
          title="Edit workout"
        >
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
          </svg>
        </button>

        {/* Delete Button */}
        <button
          className="action-btn delete-btn"
          onClick={handleDelete}
          title="Delete workout"
        >
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
          </svg>
        </button>
      </div>

      <div className="workout-header">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <h4>{workout.title}</h4>
          <span className="category-tag">{workout.category || 'Strength'}</span>
        </div>
      </div>

      <div className="workout-stats-grid">
        <div className="workout-stat">
          <span className="label">Weight / Load</span>
          <span className="value">{getDisplayLoad()}</span>
        </div>
        <div className="workout-stat">
          <span className="label">Reps Completed</span>
          <span className="value">{workout.reps} reps</span>
        </div>
      </div>

      {workout.notes && (
        <div className="workout-notes">
          {workout.notes}
        </div>
      )}

      <div className="workout-footer">
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zm0-12H5V6h14v2zm-7 5h5v5h-5z"/>
        </svg>
        <span>Logged {getFormattedDate()}</span>
      </div>
    </div>
  );
};

export default WorkoutDetails;